import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, input, Output, Signal } from '@angular/core';
import { Router } from '@angular/router';

import { DateTime } from 'luxon';

import { CurrencyCustomPipe, RelativeTimePipe } from '../../pipes';
import { GuardJobItem, GuardJobShiftItem, JOB_STATUSES, SHIFT_STATUSES } from '../../types';
import { AvatarImageComponent } from '../avatar-image/avatar-image.component';
import { CardComponent } from '../card/card.component';
import { StatusChipComponent } from '../status-chip/status-chip.component';

@Component({
    selector: 'lib-job-list-item',
    imports: [
        CommonModule,
        CardComponent,
        RelativeTimePipe,
        CurrencyCustomPipe,
        AvatarImageComponent,
        StatusChipComponent
    ],
    templateUrl: './job-list-item.component.html',
    styleUrl: './job-list-item.component.css'
})
export class JobListItemComponent {
    /* eslint-disable */
    router = inject(Router);

    job = input<GuardJobItem | null>(null);

    // Available - for browsing jobs to apply to
    // Schedule - for jobs the guard is involved in
    type = input<'available' | 'schedule-confirmed' | 'schedule-pending' | 'schedule-history' | null>('available');

    button = input<boolean>(false);
    topMargin = input<boolean>(false);
    bottomMargin = input<boolean>(true);

    maxDateChips = input<number>(3);

    displayRelativeDate = input<boolean>(true);
    displayDistance = input<boolean>(true);
    displayRate = input<boolean>(false);

    navigateToJobDetails = input<boolean>(false);
    jobDetailsBackUrl = input<string>('');

    isScheduleType: Signal<boolean> = computed(() => this.type()?.includes('schedule') || false);

    @Output() onClick: EventEmitter<number> = new EventEmitter();

    dateToday: Signal<string> = computed(() => DateTime.now().toFormat('yyyy-MM-dd'));

    //Gets a list of unique shift dates for rendering
    renderedShiftDates: Signal<string[]> = computed(
        () =>
            this.job()?.shifts.reduce((acc: string[], shift: GuardJobShiftItem) => {
                const hasDate = acc.find(date => date === shift.start_date);

                if (this.isScheduleType()) {
                    if (this.type() === 'schedule-confirmed' && shift.status === SHIFT_STATUSES.ACCEPTED) {
                        acc.push(shift.start_date);
                    } else if (
                        this.type() === 'schedule-pending' &&
                        (shift.status === SHIFT_STATUSES.PENDING || shift.status === SHIFT_STATUSES.ACCEPTED)
                    ) {
                        acc.push(shift.start_date);
                    } else if (
                        this.type() === 'schedule-history' &&
                        shift.start_date !== SHIFT_STATUSES.NOT_APPLIED &&
                        (this.job()?.status === JOB_STATUSES.COMPLETED ||
                            this.job()?.status === JOB_STATUSES.REJECTED ||
                            this.job()?.status === JOB_STATUSES.CANCELLED)
                    ) {
                        acc.push(shift.start_date);
                    }
                } else if (!hasDate) {
                    acc.push(shift.start_date);
                }
                return acc;
            }, []) || []
    );

    //Get a list of unique shift starting and ending times for rendering on the template
    renderedShiftTimes: Signal<Array<{ start_date: string; start_time: string; end_date: string; end_time: string }>> =
        computed(
            () =>
                this.job()?.shifts.reduce(
                    (
                        acc: Array<{ start_date: string; start_time: string; end_date: string; end_time: string }>,
                        shift: GuardJobShiftItem
                    ) => {
                        const hasTime = acc.find(time => time.start_time === shift.start_time);
                        if (!hasTime) {
                            acc.push({
                                start_date: shift.start_date,
                                start_time: shift.start_time,

                                end_date: shift.end_date,
                                end_time: shift.end_time
                            });
                        }

                        return acc;
                    },
                    []
                ) || []
        );

    click() {
        if (!this.button()) return;
        if (this.navigateToJobDetails()) {
            this.router.navigateByUrl(`/job/${this.job()?.job_id}`, {
                state: {
                    data: {
                        backUrl: this.jobDetailsBackUrl()
                    }
                }
            });
            return;
        } else {
            this.onClick.emit(this.job()?.job_id);
        }
    }
}
